import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { SMLCreationSheet } from '../creationSheet/SMLCreationSheet';
import { SMLAnalytics } from '../../analytics/sml/analytics';
import { SMLCreateSmartLinkButtonProps } from './props';
import { useIsDemoAccount } from '../../accountSelector/useIsDemoAccount';
import { useSmartLinkSheetSong } from '../creationSheet/useSmartLinkSheetSong';

/**
 * The primary "New smartlink" action. It carries its own creation sheet, so it
 * can sit in a page header slot instead of a tile in the card grid.
 *
 * This is the web and Electron entry point. The mobile app reaches creation
 * through `SMLCreationBox`, which still opens the older modal.
 *
 * `?catalogItemUuid=<uuid>` on the SmartLinks page opens the sheet with that catalog song picked
 * (`useOpenSmartLinkSheetForSong`). Closing the sheet takes the value out of the address.
 */
const SMLCreateSmartLinkButtonComponent: React.FC<SMLCreateSmartLinkButtonProps> = ({ accountId }) => {
  const intl = useIntl();
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  // design: music24-demo-artist a4. The demo artist is read-only.
  const isDemo = useIsDemoAccount();

  const openModal = useCallback(() => {
    SMLAnalytics.trackSmartLinkCreateOpened('picker').catch(console.error);
    setModalOpen(true);
  }, []);

  const sheetSong = useSmartLinkSheetSong();
  const songToOpen = isDemo ? undefined : sheetSong.catalogItemUuid;
  const clearSheetSong = sheetSong.clear;

  const closeModal = useCallback(() => {
    setModalOpen(false);
    if (songToOpen !== undefined) {
      clearSheetSong();
    }
  }, [clearSheetSong, songToOpen]);

  return (
    <>
      <SMLCreationSheet
        accountId={accountId}
        isOpen={isModalOpen || songToOpen !== undefined}
        closeModal={closeModal}
        initialCatalogItemUuid={songToOpen}
      />
      <SMLButton onClick={openModal} leftIcon={faPlus} testID="NewSmartLink" disabled={isDemo}>
        {intl.formatMessage({ id: 'app.smart-links.new', defaultMessage: 'New smartlink' })}
      </SMLButton>
    </>
  );
};

export const SMLCreateSmartLinkButton = TypedMemo(SMLCreateSmartLinkButtonComponent);
